import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "./api";

const AuthContext = createContext(null);

// Auth state now lives in an HttpOnly cookie set by the backend on login.
// We call /auth/me on mount to hydrate the user object; localStorage keeps
// a copy of the user profile ONLY as a cache for a snappy first paint
// (no secrets — the actual auth token is unreadable to JS).
export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem("calibrate_user");
    return raw ? JSON.parse(raw) : null;
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Cookie is sent automatically by withCredentials. If it's missing or
    // expired, /auth/me returns 401 and we clear the cached user.
    api.get("/auth/me")
      .then((r) => {
        setUser(r.data);
        localStorage.setItem("calibrate_user", JSON.stringify(r.data));
      })
      .catch(() => {
        localStorage.removeItem("calibrate_token");
        localStorage.removeItem("calibrate_user");
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    // Server sets the HttpOnly auth cookie on the response. The `token` in
    // the JSON body is legacy — kept for API compatibility but no longer
    // stored client-side.
    const r = await api.post("/auth/login", { email, password });
    localStorage.setItem("calibrate_user", JSON.stringify(r.data.user));
    setUser(r.data.user);
    return r.data.user;
  };

  const register = async (payload) => {
    const r = await api.post("/auth/register", payload);
    // New flow: register no longer issues a token. The user must verify their
    // email first. Hand the response back to the caller so it can navigate to
    // the verification-pending screen.
    return r.data;
  };

  // Used by /verify-email/:token after a successful confirmation. The server
  // has already set the auth cookie on the verify response — we only need to
  // hydrate local user state.
  const acceptToken = (_token, userObj) => {
    localStorage.setItem("calibrate_user", JSON.stringify(userObj));
    setUser(userObj);
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* clear locally even if server unreachable */ }
    localStorage.removeItem("calibrate_token");
    localStorage.removeItem("calibrate_user");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, acceptToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
